import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { Eye, EyeOff, ArrowRight, CalendarHeart, Mail } from 'lucide-react';

export default function RegisterPage() {
  const { register, loginWithGoogle, isAuthenticated } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && !registered) {
      navigate('/account');
    }
  }, [isAuthenticated, registered, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t({ it: 'Le password non corrispondono', en: 'Passwords do not match' }));
      return;
    }

    if (password.length < 6) {
      setError(t({ it: 'La password deve avere almeno 6 caratteri', en: 'Password must be at least 6 characters' }));
      return;
    }

    if (!privacyAccepted) {
      setError(t({ it: "Devi accettare l'informativa privacy per registrarti", en: 'You must accept the privacy policy to sign up' }));
      return;
    }

    setLoading(true);
    const result = await register(email, password, name, privacyAccepted);
    if (result.success) {
      setRegistered(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (registered) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24 flex items-center justify-center">
        <div className="w-full max-w-lg text-center" data-testid="register-success-message">
          <div className="w-16 h-16 mx-auto mb-8 flex items-center justify-center bg-[#4A5D23] text-white rounded-full">
            <CalendarHeart className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-6">
            {t({ it: 'Benvenuto in biolosophy!', en: 'Welcome to biolosophy!' })}
          </h1>
          <p className="text-lg text-[#1C1917]/80 leading-relaxed mb-4">
            {t({ it: 'La collezione ti sarà visibile', en: 'The collection will be visible to you' })}{' '}
            <strong>{t({ it: 'in anteprima il 15/11/2026', en: 'as a preview on 15/11/2026' })}</strong>.
          </p>
          <div className="flex items-center justify-center gap-2 text-[#57534E] mb-10">
            <Mail className="w-5 h-5 text-[#4A5D23]" />
            <p className="text-sm">
              {t({ it: 'Riceverai un avviso via email come promemoria.', en: 'You will receive an email reminder.' })}
            </p>
          </div>
          <Link
            to="/"
            data-testid="register-success-home-btn"
            className="btn-primary inline-flex items-center gap-2"
          >
            {t({ it: 'Torna alla Home', en: 'Back to Home' })}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-2">
            {t({ it: 'Crea il tuo Account', en: 'Create your Account' })}
          </h1>
          <p className="text-[#57534E]">
            {t({ it: 'Unisciti alla comunità Biolosophy', en: 'Join the Biolosophy community' })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">{t({ it: 'Nome Completo', en: 'Full Name' })}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="register-name-input"
              className="w-full px-4 py-3 border border-[#E5E0D8] focus:border-[#4A5D23] transition-colors"
              placeholder="Mario Rossi"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="register-email-input"
              className="w-full px-4 py-3 border border-[#E5E0D8] focus:border-[#4A5D23] transition-colors"
              placeholder="nome@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="register-password-input"
                className="w-full px-4 py-3 border border-[#E5E0D8] focus:border-[#4A5D23] transition-colors pr-12"
                placeholder="Minimo 6 caratteri"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#57534E]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t({ it: 'Conferma Password', en: 'Confirm Password' })}</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="register-confirm-password-input"
              className="w-full px-4 py-3 border border-[#E5E0D8] focus:border-[#4A5D23] transition-colors"
              placeholder="Ripeti la password"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-consent-label">
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              required
              data-testid="privacy-consent-checkbox"
              className="w-5 h-5 mt-0.5 accent-[#4A5D23] flex-shrink-0"
            />
            <span className="text-sm text-[#57534E] leading-relaxed">
              {t({ it: 'Ho letto e accetto la', en: 'I have read and accept the' })}{' '}
              <Link to="/privacy" target="_blank" className="text-[#4A5D23] underline">
                Privacy Policy
              </Link>{' '}
              {t({ it: 'ai sensi del Reg. UE 2016/679 (GDPR)', en: 'pursuant to EU Reg. 2016/679 (GDPR)' })} *
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? t({ it: 'Registrazione in corso...', en: 'Signing up...' }) : t({ it: 'Registrati', en: 'Sign Up' })}
            {!loading && <ArrowRight className="w-5 h-5" />}
          </button>
        </form>

        <div className="my-8 flex items-center gap-4">
          <div className="flex-1 border-t border-[#E5E0D8]" />
          <span className="text-sm text-[#57534E]">{t({ it: 'oppure', en: 'or' })}</span>
          <div className="flex-1 border-t border-[#E5E0D8]" />
        </div>

        <button
          onClick={loginWithGoogle}
          data-testid="google-register-btn"
          className="w-full py-3 border border-[#E5E0D8] hover:border-[#1C1917] flex items-center justify-center gap-3 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {t({ it: 'Registrati con Google', en: 'Sign up with Google' })}
        </button>

        <p className="text-center mt-8 text-[#57534E]">
          {t({ it: 'Hai già un account?', en: 'Already have an account?' })}{' '}
          <Link to="/login" className="text-[#4A5D23] font-medium hover:underline" data-testid="login-link">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
}
